import { useEffect, useState } from 'react';

import { useGetShipmentElabel } from './useGetShipmentElabel';
import { useObjectUrl } from './useObjectUrl';

/**
 * Hook that manages label URLs for duplicate shipment form.
 * Handles both file uploads (new labels) and fetched labels (from source shipment).
 * Returns resolved URLs prioritizing uploaded files over  fetched base64 data.
 */
export const useShipmentLabelUrls = (
  sourceShipmentId: string,
  returnShipmentId: string | undefined,
  file: File | null,
  returnFile: File | null
) => {
  const [sourceLabelDataUrl, setSourceLabelDataUrl] = useState<string | null>(
    null
  );
  const [returnLabelDataUrl, setReturnLabelDataUrl] = useState<string | null>(
    null
  );

  const getELabel = useGetShipmentElabel();
  const outboundFileUrl = useObjectUrl(file);
  const returnFileUrl = useObjectUrl(returnFile);

  // Fetch outbound e-label image from the source shipment
  useEffect(() => {
    setSourceLabelDataUrl(null);

    getELabel(sourceShipmentId)
      .then((label) => {
        if (label.img.image) {
          setSourceLabelDataUrl(`data:image/png;base64,${label.img.image}`);
        } else {
          setSourceLabelDataUrl(null);
        }
      })
      .catch(() => setSourceLabelDataUrl(null));
  }, [getELabel, sourceShipmentId]);

  // Fetch return e-label image from the source return shipment (if round-trip)
  useEffect(() => {
    if (!returnShipmentId) {
      setReturnLabelDataUrl(null);

      return;
    }

    setReturnLabelDataUrl(null);

    getELabel(returnShipmentId)
      .then((label) => {
        if (label.img.image) {
          setReturnLabelDataUrl(`data:image/png;base64,${label.img.image}`);
        } else {
          setReturnLabelDataUrl(null);
        }
      })
      .catch(() => setReturnLabelDataUrl(null));
  }, [getELabel, returnShipmentId]);

  const outboundLabelUrl = outboundFileUrl || sourceLabelDataUrl;
  const returnLabelUrl = returnFileUrl || returnLabelDataUrl;

  return {
    outboundLabelUrl,
    returnLabelUrl,
  };
};
